import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Lazy-load all page components — reduces initial bundle by ~200-300KB
// Only the visited route's code is downloaded
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CredentialsPage = lazy(() => import('./pages/CredentialsPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const FoundationPage = lazy(() => import('./pages/FoundationPage'));
const SourcesPage = lazy(() => import('./pages/SourcesPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const DraftsPage = lazy(() => import('./pages/DraftsPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const PublishingPage = lazy(() => import('./pages/PublishingPage'));
const AccountsPage = lazy(() => import('./pages/AccountsPage'));
const PeoplePage = lazy(() => import('./pages/PeoplePage'));
const ApolloPage = lazy(() => import('./pages/ApolloPage'));
const MultichannelPage = lazy(() => import('./pages/MultichannelPage'));
const PromptOpsPage = lazy(() => import('./pages/PromptOpsPage'));
const LearningPage = lazy(() => import('./pages/LearningPage'));
// vNext Agentic
const AgentPage = lazy(() => import('./pages/AgentPage'));
const AgentOpsPage = lazy(() => import('./pages/AgentOpsPage'));
const TokenUsagePage = lazy(() => import('./pages/TokenUsagePage'));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
// V1.1 Multi-tenancy
const SignupPage = lazy(() => import('./pages/SignupPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const OrgSettingsPage = lazy(() => import('./pages/OrgSettingsPage'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
// CRM + Signal Intelligence Layer
const DealsPage = lazy(() => import('./pages/DealsPage'));
const SignalCenterPage = lazy(() => import('./pages/SignalCenterPage'));
const CRMTasksPage = lazy(() => import('./pages/CRMTasksPage'));
const ScoringPage = lazy(() => import('./pages/ScoringPage'));
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'));
// Intelligence Ingestion (Cold-Start Solver)
const IntelligenceIngestionPage = lazy(() => import('./pages/IntelligenceIngestionPage'));
const CompetitiveLandscapePage = lazy(() => import('./pages/CompetitiveLandscapePage'));

// Lightweight loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 12 }}>
    <div style={{ width: 28, height: 28, border: '3px solid #E5E7EB', borderTopColor: '#0276F1', borderRadius: '50%', animation: 'spin .6s linear infinite' }} />
    <span style={{ fontSize: 12, color: '#9CA3AF', letterSpacing: '0.5px' }}>Loading...</span>
  </div>
);

export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? (
    <ErrorBoundary>
      <Layout>{children}</Layout>
    </ErrorBoundary>
  ) : <Navigate to="/login" replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tsmx_token'));
  const [loading, setLoading] = useState(true);
  const [activeCountry, setActiveCountry] = useState(
    JSON.parse(localStorage.getItem('tsmx_country') || '{"code":"MX","name":"Mexico","flag":"\uD83C\uDDF2\uD83C\uDDFD"}')
  );
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const verifyToken = async () => {
      const stored = localStorage.getItem('tsmx_token');
      if (!stored) { setLoading(false); return; }

      // Show cached user/country instantly (skip loading spinner)
      try {
        const cu = localStorage.getItem('tsmx_user');
        const cc = localStorage.getItem('tsmx_country');
        if (cu && cc) {
          setUser(JSON.parse(cu));
          setToken(stored);
          setActiveCountry(JSON.parse(cc));
          setLoading(false); // Instant render — verify in background
        }
      } catch {}

      try {
        const authHeaders = { Authorization: `Bearer ${stored}` };
        const [authRes, settingsRes] = await Promise.all([
          fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders }),
          fetch(`${API_BASE}/api/settings/me`, { headers: authHeaders }).catch(() => null),
        ]);
        if (authRes.ok) {
          const data = await authRes.json();
          setUser(data);
          setToken(stored);
          localStorage.setItem('tsmx_user', JSON.stringify(data));
          if (settingsRes?.ok) {
            const settings = await settingsRes.json();
            const country = { code: settings.active_country, name: settings.active_country_name, flag: settings.active_country_flag };
            setActiveCountry(country);
            localStorage.setItem('tsmx_country', JSON.stringify(country));
          }
        } else {
          localStorage.removeItem('tsmx_token');
          localStorage.removeItem('tsmx_user');
          setToken(null);
          setUser(null);
        }
      } catch {
        localStorage.removeItem('tsmx_token');
        localStorage.removeItem('tsmx_user');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  // Load countries list when we have a token (cached — rarely changes)
  useEffect(() => {
    if (!token) return;
    try { const c = sessionStorage.getItem('tsmx_cache_countries'); if (c) setCountries(JSON.parse(c)); } catch {}
    fetch(`${API_BASE}/api/settings/countries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { const arr = Array.isArray(data) ? data : []; setCountries(arr); try { sessionStorage.setItem('tsmx_cache_countries', JSON.stringify(arr)); } catch {} })
      .catch(() => {});
  }, [token]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('tsmx_token', authToken);
    localStorage.setItem('tsmx_user', JSON.stringify(userData));
  };

  const logout = async () => {
    // FIX 6: Call server to revoke token before clearing local state
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch { /* silent — token still cleared locally */ }
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('tsmx_token');
    localStorage.removeItem('tsmx_country');
    localStorage.removeItem('tsmx_user');
    sessionStorage.clear(); // Clear all cached API data
  };

  const switchCountry = async (countryCode) => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/me`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active_country: countryCode })
      });
      if (res.ok) {
        const data = await res.json();
        const country = { code: data.active_country, name: data.active_country_name, flag: data.active_country_flag };
        setActiveCountry(country);
        localStorage.setItem('tsmx_country', JSON.stringify(country));
      }
    } catch { /* silent */ }
  };

  // Provide country code in headers for all fetch calls
  const authHeaders = token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Country-Code': activeCountry.code
  } : {};

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-logo"><div className="logo-mark">M</div><div className="logo-text">Motion</div></div>
      <div className="loading-spinner"></div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, token, login, logout, activeCountry, countries, switchCountry, authHeaders }}>
      <Router>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute><CredentialsPage embedded /></ProtectedRoute>} />
          <Route path="/foundation" element={<ProtectedRoute><FoundationPage /></ProtectedRoute>} />
          <Route path="/sources" element={<ProtectedRoute><SourcesPage /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute><AssetsPage /></ProtectedRoute>} />
          <Route path="/analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
          <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
          <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
          <Route path="/publishing" element={<ProtectedRoute><PublishingPage /></ProtectedRoute>} />
          <Route path="/accounts" element={<ProtectedRoute><AccountsPage /></ProtectedRoute>} />
          <Route path="/people" element={<ProtectedRoute><PeoplePage /></ProtectedRoute>} />
          <Route path="/apollo" element={<ProtectedRoute><ApolloPage /></ProtectedRoute>} />
          <Route path="/multichannel" element={<ProtectedRoute><MultichannelPage /></ProtectedRoute>} />
          <Route path="/promptops" element={<ProtectedRoute><PromptOpsPage /></ProtectedRoute>} />
          <Route path="/learning" element={<ProtectedRoute><LearningPage /></ProtectedRoute>} />
          {/* vNext Agentic */}
          <Route path="/agent" element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />
          <Route path="/agent-ops" element={<ProtectedRoute><AgentOpsPage /></ProtectedRoute>} />
          <Route path="/token-usage" element={<ProtectedRoute><TokenUsagePage /></ProtectedRoute>} />
          <Route path="/channels" element={<ProtectedRoute><ChannelsPage /></ProtectedRoute>} />
          {/* V1.1 Multi-tenancy */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/settings/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
          <Route path="/settings/organization" element={<ProtectedRoute><OrgSettingsPage /></ProtectedRoute>} />
          <Route path="/superadmin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
          {/* CRM + Signal Intelligence Layer */}
          <Route path="/deals" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
          <Route path="/signals" element={<ProtectedRoute><SignalCenterPage /></ProtectedRoute>} />
          <Route path="/crm-tasks" element={<ProtectedRoute><CRMTasksPage /></ProtectedRoute>} />
          <Route path="/scoring" element={<ProtectedRoute><ScoringPage /></ProtectedRoute>} />
          <Route path="/intelligence" element={<ProtectedRoute><IntelligencePage /></ProtectedRoute>} />
          {/* Intelligence Ingestion (Cold-Start Solver) */}
          <Route path="/ingestion" element={<ProtectedRoute><IntelligenceIngestionPage /></ProtectedRoute>} />
          <Route path="/competitors" element={<ProtectedRoute><CompetitiveLandscapePage /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
          <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
        </Routes>
        </Suspense>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
